<template>
  <div class="subjects text-dark" :class="{'subjects--is-activities': isActivities}">
    <div class="subjects-header" v-if="!isActivities">
      <div class="subjects-header__left">
        <h1>{{ i18n[locale]['headline'] }}</h1>
        <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
        <DoFiltersFilter
          :title="i18n[locale]['subjects']"
          :tags="subjectTypeFilters"
          :selectedTags="selectedSubjectTypeFilters"
          @select="toggleSelectedSubjectTypeFilters"
          @clear="clearSelectedSubjectTypeFilters"
        >
        </DoFiltersFilter>
      </div>
      <div class="subjects-header__right">
        <button class="do-bttn do-bttn--large"
                @click="setImportSubjects" v-if="!isLoading && klassesWithoutSubjects.length > 0">
          {{ i18n[locale]['import_subjects'] }}
        </button>
      </div>
    </div>
    <Loader v-if="isLoading" color="blue"></Loader>
    <template v-else>
      <div v-if="klassCalIdsHashSortedOnName.length < 1">
        <h2 class="text-explainer">{{ i18n[locale]['create_classes_first'] }}</h2>
      </div>
      <div v-else class="subjects-table" :style="{'width': tableWidth}">
        <table class="d-table">
          <thead ref="tableHead">
          <tr>
            <th style="min-width: 160px;">
            </th>
            <th v-for="calId in klassCalIdsHashSortedOnName">
              <div class="column-name">{{ getCalendarName(calId) }}</div>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td></td>
            <td
              v-for="calId in klassCalIdsHashSortedOnName"
              :key="calId"
              class="td-create-bttn-container">
              <button class="do-bttn td-bttn--small"
                      @click="setCreateInCalendarId(calId)">{{ i18n[locale]['create'] }}
              </button>
            </td>
          </tr>
          <tr v-for="subject in subjectsInKlassesWithProps"
              :key="subject.id"
          >
            <th>{{ subject.name }}</th>
            <td class="td__subject"
                v-for="calId in klassCalIdsHashSortedOnName"
                :key="calId"
                v-if="subject.klasses[calId] && subject.klasses[calId].hours > -1"
                @click="setEditSubject(subject.klasses[calId])"
                :style="{'--subjectBgColor': subject.klasses[calId].color}"
                :aria-hours="subject.klasses[calId].hours"
                :content="subject.klasses[calId].v_userNames"
                v-tippy="{
                    arrow: true,
                    onShow: () => subject.klasses[calId].v_subjectHasTeachers
                  }"
                :class="[
                  {'td__subject--has-user': subject.klasses[calId].v_subjectHasTeachers},
                  {'td__subject--has-room': subject.klasses[calId].v_subjectHasRooms},
                  {'td__subject-has-other-klass': subject.klasses[calId].v_subjectOtherKlasses && subject.klasses[calId].v_subjectOtherKlasses.length > 0}
                  ]">
            </td>
            <td class="td__create-subject" v-else
                :content="createTooltip(subject, calId)"
                v-tippy="{
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window',
                  }"
                @click.self="setIsCreateSubject(subject, subject.klasses[calId])">
              <AddSubject v-if="isCreateSubject(subject, subject.klasses[calId])"
                          :subject="subject"
                          :calendar="subject.klasses[calId]"
                          @createSubject="createExistingSubjectInCal"
                          @close="closeIsCreateSubject">
              </AddSubject>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>
    <SubjectForm
      v-if="isSubjectModal"
      :propCalendarId="createInCalendarId"
      :showModal="isSubjectModal"
      :editSubject="editSubject"
      :isActivities="isActivities"
      @close="closeModal"
      @createSubject="createNewSubject"
      @updateSubject="updateSubject"
      @deleteSubject="deleteSubject">
    </SubjectForm>
  </div>
</template>

<script>
import Loader from '../common/Loader'
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import SubjectForm from '../Subjects/SubjectForm'
import AddSubject from './BulkCreateSubjectsSubject'
import eventbus from '../../eventbus'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'

const I18N = {
  da: {
    headline: 'Fagoprettelse',
    create_subject: 'Opret fag',
    create_classes_first: 'Opret klasser i sidemenuen først',
    import_subjects: 'Importer fag',
    subjects: 'Fag',
    no_subjects: 'Ingen fag',
    create: 'Opret',
    in: 'i'
  },
  en: {
    headline: 'Create course',
    create_subject: 'Create course',
    create_classes_first: 'Start by creating grades/groups',
    import_subjects: 'Import courses',
    subjects: 'Courses',
    no_subjects: 'No courses',
    create: 'Create',
    in: 'in'
  },
  nb: {
    headline: 'Fag- og timefordeling',
    create_subject: 'Opprett fag',
    create_classes_first: 'Opprett klasser i sidemenyen',
    import_subjects: 'Importer fag',
    no_subjects: 'Ingen fag',
    create: 'Opprett',
    in: 'i'
  }
}

export default {
  components: {DoFiltersFilter, Loader, AddSubject, SubjectForm, ChangeSchoolYear},
  props: {
    isActivities: {
      default: false
    },
    propIsSubjectModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      tableWidth: '100%',
      editSubject: null,
      isSubjectModal: false,
      isTheCreateCalenderSubjectActive: null,
      isSubjectImportModal: false,
      createInCalendarId: null
    }
  },
  computed: {
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    locale () {
      return this.$store.getters.locale
    },
    isLoading () {
      return this.$store.state.subjects.isLoading
    },
    subjectTypes () {
      return this.$store.state.subjects.subjectTypes[this.locale]
    },
    subjectTypeFilters () {
      const excluded = ['other_tasks', 'other_stuff', 'club']

      return this.subjectTypes
        .filter(type => !excluded.includes(type.value))
        .map(type => type.name)
    },
    subjectsInKlasses () {
      return this.$store.getters.uniqueSubjects ? this.$store.getters.uniqueSubjects : []
    },
    subjectsInKlassHash () {
      return this.$store.getters.subjectsInKlassHash
    },
    klassesWithoutSubjects () {
      return this.klassCalendarsSorted.filter(klass => !this.subjectsInKlassHash[klass.id])
    },
    subjectsInKlassesWithProps () {
      return this.subjectsInKlasses.map(subject => {
        Object.values(subject.klasses).forEach(cal => {
          const calId = cal.calendar_id
          if (subject.klasses[calId].calendar_ids) {
            this.$set(subject['klasses'][calId], 'v_subjectHasTeachers', this.subjectHasTeachers(subject.klasses[calId].calendar_ids))
            this.$set(subject['klasses'][calId], 'v_subjectHasRooms', this.subjectHasRooms(subject.klasses[calId].calendar_ids))
            this.$set(subject['klasses'][calId], 'v_subjectOtherKlasses', this.subjectOtherKlasses(subject.klasses[calId], subject.klasses[calId].calendar_ids))
            this.$set(subject['klasses'][calId], 'v_userNames', this.getUserNames(subject.klasses[calId].calendar_ids))
          }
        })
        return subject
      })
    },
    klassCalendarsSorted () {
      return this.$store.getters.klassCalendars
    },
    klassCalIdsHashSortedOnName () {
      return this.$store.getters.klassCalIdsHashSortedOnName
    },
    klassCalendarInHash () {
      return this.$store.getters.allKlassCalendarsInHash
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    i18n () {
      return I18N
    },
    selectedSubjectTypeFilters () {
      return this.$store.state.subjects.selectedSubjectTypeFilters
    }
  },
  watch: {
    activeSchoolYear () {
      this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
    },
    propIsSubjectModal (bool) {
      this.isSubjectModal = bool
    }
  },
  directives: {
    focus: {
      // directive definition
      inserted: function (el) {
        el.focus()
      }
    }
  },
  methods: {
    createTooltip (subject, calId) {
      const locale = this.locale || 'en' // fallback hvis locale mangler
      const translations = this.i18n && this.i18n[locale] ? this.i18n[locale] : {}

      const subjectName = subject && subject.name ? subject.name : ''
      const calendarName = typeof this.getCalendarName === 'function' ? this.getCalendarName(calId) : ''

      const createText = translations['create'] || 'Create'
      const inText = translations['in'] || 'in'
      return createText + ' ' + subjectName + ' ' + inText + ' ' + calendarName
    },
    setCreateInCalendarId (calId) {
      this.createInCalendarId = calId
      if (calId) {
        this.isSubjectModal = true
      } else {
        this.isSubjectModal = false
      }
    },
    toggleSelectedSubjectTypeFilters (filter) {
      this.$store.commit('TOGGLE_SUBJECT_TYPE_FILTER', filter)
    },
    clearSelectedSubjectTypeFilters () {
      this.$store.commit('CLEAR_SUBJECT_TYPE_FILTERS')
    },
    setImportSubjects () {
      eventbus.theKlassesToImportSubjectTo = [...this.klassesWithoutSubjects]
      eventbus.createKlassModal = true
    },
    subjectHasTeachers (calIds) {
      let hasTeachers = false
      calIds.forEach(calId => {
        if (this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'user') {
          hasTeachers = true
        }
      })
      return hasTeachers
    },
    subjectHasRooms (calIds) {
      let hasRooms = false
      calIds.forEach(calId => {
        if (this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'room') {
          hasRooms = true
        }
      })
      return hasRooms
    },
    subjectOtherKlasses (subject, calIds) {
      if (!calIds || calIds.length < 1 || !calIds[0]) {
        return ''
      }
      return calIds.filter(calId => calId !== subject.calendar_id && this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'klass').map(userCalId => this.allCalendarsInHash[userCalId].name ? this.allCalendarsInHash[userCalId].name : '')
    },
    getUserNames (calIds) {
      if (!calIds || calIds.length < 1 || !calIds[0]) {
        return ''
      }
      return calIds.filter(calId => this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'user').map(userCalId => this.allCalendarsInHash[userCalId].name ? this.allCalendarsInHash[userCalId].name : '')
    },
    getCalendarName (calId) {
      return this.klassCalendarInHash[calId] ? this.klassCalendarInHash[calId].name : 'id: ' + calId
    },
    setIsCreateSubject (subject, calendarSubject) {
      setTimeout(_ => {
        this.isTheCreateCalenderSubjectActive = subject.id + '_' + calendarSubject.calendar_id
      }, 0)
    },
    isCreateSubject (subject, calendarSubject) {
      return this.isTheCreateCalenderSubjectActive === subject.id + '_' + calendarSubject.calendar_id
    },
    closeIsCreateSubject () {
      this.isTheCreateCalenderSubjectActive = null
    },
    closeModal () {
      this.editSubject = null
      this.isSubjectModal = false
      this.createInCalendarId = null
      this.$emit('closeSubjectModal')
    },
    setEditSubject (editSubject) {
      this.editSubject = Object.assign({}, editSubject)
      this.isSubjectModal = true
      setTimeout(_ => {
        this.isTheCreateCalenderSubjectActive = null
      }, 1)
    },
    createNewSubject (newSubject) {
      newSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('createSubject', newSubject)
      this.closeModal()
    },
    createExistingSubjectInCal (brik) {
      let newSubject = {
        calendarId: brik.calendar.calendar_id,
        name: brik.subject.name,
        color: brik.subject.color,
        defaultDuration: brik.subject.default_duration,
        preparation: brik.subject.preparation ? brik.subject.preparation : null,
        hours: brik.hours,
        subjectType: brik.subject.subject_type,
        calendarIds: [],
        subjectCode: brik.subject.subject_code
      }
      newSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('createSubject', newSubject)
    },
    updateSubject (updatedSubject) {
      updatedSubject['schoolId'] = updatedSubject.schoolId ? updatedSubject.schoolId : window.SCHOOL.id
      updatedSubject['schoolYearId'] = updatedSubject.schoolYearId ? updatedSubject.schoolYearId : this.activeSchoolYear.id
      // console.log('findOtherKlassesOnSubject (subject)', this.findOtherKlassesOnSubject(updatedSubject))
      this.$store.dispatch('updateSubject', updatedSubject)
      this.closeModal()
    },
    findOtherKlassesOnSubject (subject) {
      // Find klasser
      // Find ud af om klassen allerede har det fag
      // Se om det er noget nyt at der er sat en klasse på (se om det er det samme fag)
      const calIds = subject.calendarIds
      const idx = this.subjectsInKlasses.findIndex(subjectWithKlasses => subjectWithKlasses.name === subject.name)
      if (!calIds || calIds.length < 1 || !calIds[0]) {
        return ''
      }
      return calIds.filter(calId => calId !== subject.calendar_id && this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'klass' && this.subjectsInKlasses[idx].klasses[calId] && this.subjectsInKlasses[idx].klasses[calId].hours)
    },
    deleteSubject (editSubject) {
      let deletedSubject = {
        id: editSubject.id,
        school_id: editSubject.school_id,
        school_year_id: this.activeSchoolYear.id
      }
      deletedSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('deleteSubject', deletedSubject)
      this.closeModal()
    },
    setTableWidth () {
      const el = this.$refs.tableHead
      if (!el) {
        return
      }
      let tableBCR = el.getBoundingClientRect()
      this.tableWidth = tableBCR.width + 15 + 'px'
    },
    readSubject () {
      this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
    }
  },
  mounted () {
    window.addEventListener('new_subjects_created', () => this.readSubject())
  },
  beforeDestroy () {
    window.removeEventListener('new_subjects_created', () => this.readSubject())
  },
  updated () {
    this.setTableWidth()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.subjects {
  min-height: 400px;
}

.subjects--is-activities {
  display: flex;
  flex-direction: column;
}

.subjects-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.subjects-header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.subjects-header__left > h1 {
  margin-right: 20px;
}

.subjects-header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.subjects-table {
  max-width: 100%;
  max-height: calc(100vh - (40px + 55px + 55px + 20px + 20px));
  overflow: scroll;
  position: relative;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
}

.d-table {
  position: relative;
  max-width: 100%;
  color: #3d4340;
  border-collapse: collapse;
}

.d-table thead th {
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  z-index: 1;
  top: 0;
  background: #f6f9fc;
  border-bottom: 1px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: .075rem;
  font-size: 1rem;
  border-collapse: collapse;
}

.d-table thead tr th:first-child, tbody tr th:first-child {
  left: 0;
  z-index: 1;
  width: 120px;
  text-align: left;
  padding: 0 8px;
}

.d-table tbody th {
  background-color: #ffffff;
  position: -webkit-sticky; /* for Safari */
  position: sticky;
  z-index: 2;
  left: 0;
  border-collapse: collapse;
}

.column-name {
  max-width: 130px;
  min-width: 110px;
}

.d-table tr td, .d-table tr th {
  height: 40px;
}

.d-table tr td, .d-table tr th {
  transition: background-color 0.15s;
}

.d-table tbody tr:hover td, .d-table tbody tr:hover th {
  background-color: aliceblue;
}

.td__subject {
  position: relative;
}

.td__subject:before {
  content: attr(aria-hours);
  position: absolute;
  top: 4px;
  left: 2px;
  right: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: var(--subjectBgColor);
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  opacity: 0.9;
  text-align: left;
}

.td__subject--has-room:after {
  content: "\ea38";
  position: absolute;
  top: 12px;
  right: 8px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.td__subject-has-other-klass:after {
  content: "\EA62";
  position: absolute;
  top: 12px;
  right: 8px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.td__subject--has-user:after {
  content: "\eaf7";
  position: absolute;
  top: 12px;
  right: 8px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.td__subject--has-room.td__subject--has-user:not(.td__subject--has-other-klass):after {
  content: '';
  width: 18px;
  top: 14px;
  height: 18px;
  background-image: url('https://app.docendo.dk/static/img/app/bulk_create_subjects_userlocation.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__subject-has-other-klass.td__subject--has-user:not(.td__subject--has-room):after {
  content: '';
  width: 22px;
  top: 14px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_userklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__subject-has-other-klass.td__subject--has-room:not(.td__subject--has-user):after {
  content: '';
  width: 18px;
  top: 14px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_locationklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__subject-has-other-klass.td__subject--has-room.td__subject--has-user:after {
  content: '';
  width: 29px;
  top: 14px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_userlocationklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.td__create-subject {
  position: relative;
  cursor: pointer;
}

.td__create-subject:before {
  content: attr(aria-hours);
  position: absolute;
  top: 4px;
  left: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 28px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #eeeeee;
  overflow: hidden;
  opacity: 0.9;
  text-align: left;
  transition: box-shadow 0.25s, border-color 0.25s;
  border: 1px solid transparent;
}

.td__create-subject:hover:before {
  content: '';
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  border-color: #468AD7;
}

.td__create-subject:after {
  content: '+';
  position: absolute;
  top: 8px;
  right: 0;
  left: 0;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dark-blue);
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background-color: #c0c0c0;
}

.subject {
  position: absolute;
  z-index: 12;
  top: 4px;
  left: 2px;
  right: 2px;
  bottom: 0;
  padding: 4px 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  text-align: left;
}

.text-explainer {
  font-size: 20px;
  font-style: italic;
}

.list-enter-active, .list-leave-active {
  transition: opacity 0.45s, transform 0.45s;
}

.list-enter, .list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.bttn.create-btn--secondary {
  height: 38px;
  padding: 2px 16px;
}

.do-input-and-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
}

.do-input-and-button > input {
  min-width: 220px;
  max-width: 220px;
  max-height: 28px;
}

.do-input-and-button > .icon__bg-circle {
  min-width: 30px;
}

.td-bttn--small {
  width: calc(100% - 1rem);
}

.td-create-bttn-container {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// BulkCreateSubjects.vue?b707116e